// The outbox's decisions, kept apart from its requests and storage so they can be tested
// on their own (see chatOutboxRules.test.js)

// Who is logged in, not the token itself: a refreshed token (the API's, or the socket's
// renewal) is still the same login and keeps the queue. A new sign-in (a new session),
// another user or another company is a different one. "" when signed out; a token without
// a user id (an older one) is compared whole
export const loginIdentity = (jwt, payload) => {
  if (!jwt) return "";
  return payload?.userId ? [payload.userId, payload.companyId, payload.sid].join(":") : jwt;
};

// Errors a retry can fix: the session being renewed, a timeout, too many requests, or no
// answer / a server error
const RETRYABLE = [401, 408, 429];
// The server refused it for good (bad file, no longer in the chat...) - retrying can't help
export const isPermanent = (err) => {
  const status = err?.response?.status;
  return status >= 400 && status < 500 && !RETRYABLE.includes(status);
};

// Why a message refused for good wasn't sent, as the user reads it under "Not sent": the
// server's own words when it gave some, else by what kind of refusal it was
export const NOT_SENT_DEFAULT = "This message couldn't be sent.";
export const notSentReason = (err) => {
  const said = err?.response?.data?.message;
  if (typeof said === "string" && said.trim()) return said.trim();
  const status = err?.response?.status;
  if (status === 403) return "You can no longer send messages in this chat.";
  if (status === 404) return "This chat is no longer available.";
  if (status === 413) return "This file is too big to send.";
  if (status === 415) return "This type of file can't be sent.";
  return NOT_SENT_DEFAULT;
};

// How a send refused for good shows: "Not sent", with why. Anything else (no internet, the
// server down or busy) never fails - it keeps its clock and goes by itself (chatOutbox)
export const failedSend = (err) => ({ failed: true, permanent: true, error: notSentReason(err) });

// Trying again by itself: soon at first, then less often, at most once a minute. The
// connection coming back (or the tab back in front) tries at once instead
export const RETRY_DELAYS_MS = [2000, 5000, 15000, 30000, 60000];
export const retryDelay = (attempt) =>
  RETRY_DELAYS_MS[Math.min(Math.max(attempt, 0), RETRY_DELAYS_MS.length - 1)];

export const ALREADY_SENT_NOTICE = "That message had already been sent. You can delete it from its menu.";

// What happens to a message the server has just accepted. It's real now - the other
// person may already have it - so it's never taken back down by itself, not even one
// discarded while it was on its way: that one shows as sent, and the user is told.
// Only a change of login meanwhile leaves it out (it's no longer this queue's to show)
export const afterSend = ({ loginChanged, discarded }) => ({
  show: !loginChanged,
  notice: !loginChanged && discarded ? ALREADY_SENT_NOTICE : null,
});
